/**
 * UserListPagination - Reusable pagination component with page size selector
 *
 * Features:
 * - Page size selector (10, 20, 100)
 * - Previous/Next navigation
 * - Shows current range and total
 * - Responsive design (mobile-friendly)
 */

import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export interface PaginationState {
  page: number;
  limit: number;
  total: number;
}

export interface UserListPaginationProps {
  pagination: PaginationState;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  pageSizeOptions?: number[];
  loading?: boolean;
  className?: string;
}

const DEFAULT_PAGE_SIZES = [10, 20, 100];

export function UserListPagination({
  pagination,
  onPageChange,
  onLimitChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  loading = false,
  className = ''
}: UserListPaginationProps) {
  const { page, limit, total } = pagination;
  const totalPages = Math.ceil(total / limit);
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  const canGoPrevious = page > 1;
  const canGoNext = page < totalPages;

  const handlePrevious = () => {
    if (canGoPrevious && !loading) {
      onPageChange(page - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext && !loading) {
      onPageChange(page + 1);
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(e.target.value, 10);
    onLimitChange(newLimit);
  };

  // Don't show pagination if no items or only one page with few items
  if (total === 0) {
    return null;
  }

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 bg-white px-4 py-3 border border-gray-200 rounded-lg ${className}`}>
      {/* Left side: Page size selector and info */}
      <div className="flex items-center gap-4">
        {/* Page size selector */}
        <div className="flex items-center gap-2">
          <label htmlFor="page-size" className="text-sm text-gray-600">
            Show
          </label>
          <select
            id="page-size"
            value={limit}
            onChange={handleLimitChange}
            disabled={loading}
            className="block w-20 rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-600">per page</span>
        </div>

        {/* Showing info */}
        <div className="hidden sm:block text-sm text-gray-600">
          <span className="font-medium">{startItem}</span>
          {' - '}
          <span className="font-medium">{endItem}</span>
          {' of '}
          <span className="font-medium">{total}</span>
        </div>
      </div>

      {/* Right side: Navigation */}
      <div className="flex items-center gap-2">
        {/* Mobile: Simple info */}
        <div className="sm:hidden text-sm text-gray-600">
          Page {page} of {totalPages}
        </div>

        {/* Navigation buttons */}
        <nav className="flex items-center gap-1" aria-label="Pagination">
          <button
            onClick={handlePrevious}
            disabled={!canGoPrevious || loading}
            className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeftIcon className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Previous</span>
          </button>

          {/* Page numbers (desktop only) */}
          <div className="hidden md:flex items-center gap-1">
            {generatePageNumbers(page, totalPages).map((pageNum, idx) => (
              pageNum === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>
              ) : (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum as number)}
                  disabled={loading}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    pageNum === page
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  } disabled:opacity-50`}
                >
                  {pageNum}
                </button>
              )
            ))}
          </div>

          <button
            onClick={handleNext}
            disabled={!canGoNext || loading}
            className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRightIcon className="h-4 w-4 ml-1" />
          </button>
        </nav>
      </div>
    </div>
  );
}

/**
 * Generate page numbers with ellipsis for large page counts
 */
function generatePageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];

  // Always show first page
  pages.push(1);

  if (currentPage > 3) {
    pages.push('...');
  }

  // Show pages around current
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) {
    if (!pages.includes(i)) {
      pages.push(i);
    }
  }

  if (currentPage < totalPages - 2) {
    pages.push('...');
  }

  // Always show last page
  if (!pages.includes(totalPages)) {
    pages.push(totalPages);
  }

  return pages;
}

export default UserListPagination;
